/**
 * lib/shopifySync.js
 * Shopify → Supabase sipariş senkronizasyonu.
 *
 * Akış:
 *   1. Shopify'dan siparişler çekilir (tam veya artımlı, GraphQL cursor sayfalama)
 *   2. Line item SKU ↔ product_variants.sku / products.sku / artikelnummer,
 *      Fallback Shopify-Varianten-ID (bkz. lib/shopifyLineItemMatch.js)
 *      → depo/stok/Lagerort/Historie entegrasyonu için product_id dolar
 *   3. Yeni siparişler orders tablosuna source='shopify' olarak eklenir
 *      → açık siparişler status='Neu' ile OTOMATİK depo emri olur
 *        (Warenausgang 'Neu' statüsündeki siparişleri listeler)
 *   4. Mevcut siparişlerde Shopify tarafında iptal/iade varsa ve lokal
 *      işlem başlamamışsa (status 'Neu') otomatik Storniert yapılır.
 *
 * Duplicate koruması: platform_order_id + source üzerinde uygulama seviyesi
 * dedup + DB'de unique partial index (bkz. migrations/20260701_shopify_sync.sql).
 */

import { initSupabase } from '@/api/adapters/SupabaseAdapter';
import { fetchAllShopifyOrders } from '@/lib/shopifyClient';
import { reserveOrderItems, reserveStock, releaseStock } from '@/lib/stockApi';
import {
  buildProductIndex,
  matchLineItem,
  stockVariantValue,
  variantLabel,
  mergeMatchedItems,
} from '@/lib/shopifyLineItemMatch';
import { logger } from '@/lib/logger';

const LAST_SYNC_KEY = 'shopify_last_sync_at';
const SOURCE = 'shopify';

// Aynı anda tek senkron çalışsın (modül seviyesi kilit)
let syncRunning = false;

function getSupabase() {
  const url = import.meta.env?.VITE_SUPABASE_URL;
  const key = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  if (!url || !key) return null;
  return initSupabase(url, key);
}

export function getLastSyncAt() {
  try {
    return localStorage.getItem(LAST_SYNC_KEY);
  } catch {
    return null;
  }
}

function setLastSyncAt(iso) {
  try {
    localStorage.setItem(LAST_SYNC_KEY, iso);
  } catch {
    /* localStorage yoksa artımlı senkron yerine tam senkron yapılır */
  }
}

// ─── Mapping ────────────────────────────────────────────────────────────────

/**
 * Shopify lifecycle → uygulama lifecycle statüsü.
 * Açık siparişler 'Neu' → otomatik depo emri (Warenausgang kuyruğu).
 */
function mapStatus(node) {
  if (node.cancelledAt) return 'Storniert';
  const fin = node.displayFinancialStatus || '';
  if (fin === 'REFUNDED' || fin === 'VOIDED') return 'Storniert';
  if (node.displayFulfillmentStatus === 'FULFILLED') return 'Abgeschlossen';
  return 'Neu';
}

function formatAddress(addr) {
  if (!addr) return null;
  return [
    addr.name,
    addr.company,
    [addr.address1, addr.address2].filter(Boolean).join(' '),
    `${addr.zip || ''} ${addr.city || ''}`.trim(),
    addr.country,
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * Shopify GraphQL order node → orders tablosu kaydı.
 * @param {object} node  - GraphQL order node
 * @param {ReturnType<typeof buildProductIndex>} index - Produkt-/Varianten-Index
 */
export function mapShopifyOrder(node, index) {
  const isEbay = (node.note || '').includes('Importiert von eBay')
    || (node.paymentGatewayNames || []).some((g) => g.toLowerCase() === 'ebay');

  const items = (node.lineItems?.nodes || []).map((li) => {
    const hit = matchLineItem(li, index);
    return {
      product_id: hit.product_id,
      product_name: li.title,
      product_sku: li.sku || null,
      // variant_value = Wert für die Lager-RPCs (nur wenn products.variants ihn kennt),
      // variant_label = Anzeigetitel aus Shopify („50 Stück", „1 Rolle" …)
      variant_value: stockVariantValue(hit.product, li.variantTitle, hit.variant?.title),
      variant_label: variantLabel(li.variantTitle, hit.variant?.title),
      shopify_variant_id: li.variant?.id || null,
      quantity: li.quantity,
      unit_price: parseFloat(li.discountedUnitPriceSet?.shopMoney?.amount || 0),
      unit: hit.unit,
    };
  });

  const customerName =
    node.shippingAddress?.name ||
    node.billingAddress?.name ||
    [node.customer?.firstName, node.customer?.lastName].filter(Boolean).join(' ') ||
    node.email ||
    'Shopify Kunde';

  const total = parseFloat(node.totalPriceSet?.shopMoney?.amount || 0);
  const subtotal = parseFloat(node.subtotalPriceSet?.shopMoney?.amount || 0);
  const tax = parseFloat(node.totalTaxSet?.shopMoney?.amount || 0);
  const status = mapStatus(node);
  const nowIso = new Date().toISOString();

  return {
    order_number: `SH-${(node.name || '').replace('#', '') || node.legacyResourceId}`,
    order_type: 'Bestellung',
    order_status: 'completed',
    // eBay üzerinden gelen Shopify siparişleri 'ebay' olarak etiketlenir —
    // depo ekranları kaynak bazlı ayrıştırma yapar.
    source: isEbay ? 'ebay' : SOURCE,
    platform_order_id: String(node.legacyResourceId),
    priority: isEbay ? 3 : 2,
    status,
    lead_name: customerName,
    lead_email: node.customer?.email || node.email || null,
    lead_phone: node.shippingAddress?.phone || node.customer?.phone || null,
    lead_address: formatAddress(node.shippingAddress),
    lieferadresse: formatAddress(node.shippingAddress),
    agent_name: isEbay ? 'Shopify (eBay)' : 'Shopify Webshop',
    positionen: items,
    items,
    subtotal,
    tax_amount: tax,
    tax_rate: subtotal > 0 && tax > 0 ? Math.round((tax / subtotal) * 10000) / 100 : 19,
    total,
    gesamtsumme: total,
    zahlungsart: (node.paymentGatewayNames || []).join(', ').slice(0, 50) || null,
    notizen: node.note || null,
    shopify_financial_status: node.displayFinancialStatus || null,
    shopify_fulfillment_status: node.displayFulfillmentStatus || null,
    shopify_synced_at: nowIso,
    created_date: node.createdAt,
    updated_date: nowIso,
  };
}

// ─── Sync engine ────────────────────────────────────────────────────────────

/**
 * Shopify siparişlerini Supabase'e senkronize eder.
 *
 * @param {object} opts
 * @param {boolean} opts.full - true: tüm geçmiş; false: son senkrondan beri değişenler
 * @param {(msg: string) => void} opts.onProgress
 * @returns {Promise<{imported:number, updated:number, cancelled:number, skipped:number, total:number}>}
 */
export async function syncShopifyOrders({ full = false, onProgress } = {}) {
  if (syncRunning) {
    return { imported: 0, updated: 0, cancelled: 0, skipped: 0, total: 0, alreadyRunning: true };
  }
  syncRunning = true;
  const progress = typeof onProgress === 'function' ? onProgress : () => {};

  try {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase bağlantısı yok (.env kontrol edin)');

    const startedAt = new Date().toISOString();
    const lastSync = getLastSyncAt();
    const updatedAtMin = full ? null : lastSync;

    // 1) Shopify'dan çek
    progress(updatedAtMin ? 'Değişen siparişler çekiliyor…' : 'Tüm siparişler çekiliyor…');
    const nodes = await fetchAllShopifyOrders({
      updatedAtMin,
      onProgress: (n) => progress(`${n} sipariş çekildi…`),
    });

    if (nodes.length === 0) {
      setLastSyncAt(startedAt);
      return { imported: 0, updated: 0, cancelled: 0, skipped: 0, total: 0 };
    }

    // 2) Ürün + varyant indeksi (SKU / Shopify-Varianten-ID → product)
    const [{ data: products, error: prodErr }, { data: variants, error: varErr }] = await Promise.all([
      supabase.from('products').select('id, sku, artikelnummer, einheit, variants'),
      supabase.from('product_variants').select('id, product_id, sku, title, shopify_variant_id, aktiv'),
    ]);
    if (prodErr) throw new Error('Produkte laden: ' + prodErr.message);
    if (varErr) throw new Error('Varianten laden: ' + varErr.message);
    const index = buildProductIndex({ products: products || [], variants: variants || [] });

    // 3) Mevcut Shopify siparişleri (dedup + statü güncelleme + geç eşleştirme)
    const { data: existing, error: exErr } = await supabase
      .from('orders')
      .select('id, platform_order_id, status, lead_id, positionen')
      .in('source', ['shopify', 'ebay']);
    if (exErr) throw new Error('Bestellungen laden: ' + exErr.message);
    const existingMap = new Map((existing || []).map((o) => [o.platform_order_id, o]));

    // 3b) Kontakt eşleştirme: e-posta → lead haritası.
    // Marketplace müşterisi kontakt listesinde yoksa otomatik oluşturulur
    // (Kundentyp = Shopify/eBay) ve sipariş kontakta bağlanır.
    const { data: leads, error: leadErr } = await supabase
      .from('leads')
      .select('id, email');
    if (leadErr) throw new Error('Kontakte laden: ' + leadErr.message);
    const leadByEmail = new Map(
      (leads || [])
        .filter((l) => l.email)
        .map((l) => [String(l.email).trim().toLowerCase(), l.id])
    );

    /** Sipariş için lead bulur/oluşturur; lead id veya null döner. */
    const ensureLead = async (node, record) => {
      const email = (record.lead_email || '').trim().toLowerCase();
      if (!email) return null;
      if (leadByEmail.has(email)) return leadByEmail.get(email);

      const addr = node.shippingAddress || {};
      const { data: created, error } = await supabase
        .from('leads')
        .insert([{
          name: record.lead_name,
          email: record.lead_email,
          telefon: record.lead_phone,
          strasse: [addr.address1, addr.address2].filter(Boolean).join(' ') || null,
          plz: addr.zip || null,
          ort: addr.city || null,
          land: addr.country || 'Deutschland',
          kundentyp: record.source === 'ebay' ? 'eBay' : 'Shopify',
          status: 'Kunde',
          quelle: 'Shopify Sync',
          notizen: 'Automatisch aus Shopify-Bestellung angelegt',
        }])
        .select('id')
        .single();
      if (error) {
        logger?.warn?.('[shopifySync] Kontakt oluşturulamadı', email, error.message);
        return null;
      }
      leadByEmail.set(email, created.id);
      return created.id;
    };

    let imported = 0;
    let updated = 0;
    let cancelled = 0;
    let skipped = 0;
    let relinked = 0;

    for (const node of nodes) {
      const record = mapShopifyOrder(node, index);
      const prev = existingMap.get(record.platform_order_id);

      if (!prev) {
        // Yeni sipariş → kontakta bağla + insert ('Neu' ise otomatik depo emri)
        record.lead_id = await ensureLead(node, record);
        const { data: inserted, error } = await supabase
          .from('orders')
          .insert([record])
          .select('id')
          .single();
        if (error) {
          // Unique index yarışı vb. — atla ama logla
          logger?.warn?.('[shopifySync] insert atlandı', record.order_number, error.message);
          skipped += 1;
          continue;
        }
        imported += 1;

        // Agent siparişleriyle AYNI kural: açık sipariş girişte stok rezerve
        // eder (allowNegative → yetersizse backorder kaydı). SKU eşleşmeyen
        // kalemler (product_id null) reserveOrderItems tarafından atlanır.
        if (record.status === 'Neu' && inserted?.id && Array.isArray(record.items)) {
          try {
            await reserveOrderItems({
              items: record.items.map((it) => ({
                productId: it.product_id,
                variantValue: it.variant_value ?? null,
                quantity: Number(it.quantity) || 0,
              })),
              orderId: inserted.id,
              actorEmail: 'shopify-sync',
              allowNegative: true,
            });
          } catch (reserveErr) {
            // Rezervasyon düşse de sipariş depo kuyruğunda kalır; stok en geç
            // commit_stock (Warenausgang) aşamasında doğru düşer.
            logger?.warn?.('[shopifySync] rezervasyon hatası', record.order_number, reserveErr.message);
          }
        }
        continue;
      }

      // Mevcut sipariş → Shopify statü + kanal alanlarını tazele.
      // (source/priority: eski importlarda eBay siparişleri 'shopify'
      // etiketliydi — bir sonraki senkronda doğru kanala taşınır.)
      const patch = {
        shopify_financial_status: record.shopify_financial_status,
        shopify_fulfillment_status: record.shopify_fulfillment_status,
        shopify_synced_at: record.shopify_synced_at,
        source: record.source,
        priority: record.priority,
        agent_name: record.agent_name,
      };

      // Kontakta henüz bağlanmamış eski importları da bağla
      if (!prev.lead_id) {
        const leadId = await ensureLead(node, record);
        if (leadId) patch.lead_id = leadId;
      }

      // Eski importlarda product_id boş kalan kalemleri (artikelnummer-only
      // eşleştirme dönemi) şimdi ürüne bağla → Lagerort/Historie/Bestand çalışır.
      // Sadece 'Neu' siparişlerde stok rezerve edilir; kapanmış/işlemdeki
      // siparişlerde yalnızca bağlantı tamamlanır (stok geçmişe dönük oynanmaz).
      const storedItems = parseStoredItems(prev.positionen);
      const { items: mergedItems, newlyMatched } = mergeMatchedItems(storedItems, record.items);
      if (newlyMatched.length > 0) {
        patch.positionen = mergedItems;
        patch.items = mergedItems;
        relinked += newlyMatched.length;
        if (prev.status === 'Neu') {
          await reserveMatchedItems(newlyMatched, prev.id, record.order_number);
        }
      }

      // Shopify'da iptal/iade edildi ve lokal işlem başlamadıysa → Storniert.
      // Depo süreci başladıysa (Kommissioniert+) OTOMATİK iptal edilmez —
      // yalnızca not düşülür, karar kullanıcıya bırakılır.
      const shopifyCancelled = record.status === 'Storniert';
      if (shopifyCancelled && prev.status === 'Neu') {
        patch.status = 'Storniert';
        cancelled += 1;
        // Girişte yapılan rezervasyonu geri bırak (idempotent — rezerv yoksa no-op)
        try {
          await releaseStock({ orderId: prev.id, actorEmail: 'shopify-sync' });
        } catch (releaseErr) {
          logger?.warn?.('[shopifySync] release hatası', prev.id, releaseErr.message);
        }
      } else if (shopifyCancelled && !['Storniert', 'Abgeschlossen'].includes(prev.status)) {
        patch.notizen = `⚠️ Shopify'da storniert/erstattet (${record.shopify_financial_status})`
          + (record.notizen ? `\n${record.notizen}` : '');
      }

      const { error } = await supabase.from('orders').update(patch).eq('id', prev.id);
      if (error) {
        logger?.warn?.('[shopifySync] update hatası', prev.id, error.message);
        skipped += 1;
      } else {
        updated += 1;
      }
    }

    setLastSyncAt(startedAt);
    progress('Senkron tamamlandı');
    return { imported, updated, cancelled, skipped, relinked, total: nodes.length };
  } finally {
    syncRunning = false;
  }
}

/** orders.positionen JSONB → dizi (string olarak gelirse parse eder). */
function parseStoredItems(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Geç eşleşen kalemleri TEK TEK rezerve eder. reserveOrderItems kullanılmaz:
 * onun rollback'i siparişin TÜM rezervlerini bırakır ve daha önce eşleşmiş
 * kalemlerin rezervini de silerdi. Hata kalem bazında loglanır, senkron sürer.
 */
async function reserveMatchedItems(items, orderId, orderNumber) {
  for (const it of items) {
    const quantity = Number(it.quantity) || 0;
    if (!it.product_id || quantity <= 0) continue;
    try {
      await reserveStock({
        productId: it.product_id,
        variantValue: it.variant_value ?? null,
        quantity,
        orderId,
        actorEmail: 'shopify-sync',
        allowNegative: true,
      });
    } catch (err) {
      logger?.warn?.('[shopifySync] geç rezervasyon hatası', orderNumber, it.product_sku, err.message);
    }
  }
}

export function isSyncRunning() {
  return syncRunning;
}
